import { Link, usePoll } from '@inertiajs/react';
import { CheckCircle2, ChevronRight, Mic } from 'lucide-react';
import type { JudgeProfile, JudgeStageSummary } from '@/types';
import { Heading } from './Heading';

// The judge's home: one card per stage assigned to them, each opening its ballot.
// Polled, so "on stage now" keeps up while the judge waits here.
export function StageList({
  judge,
  stages,
  eventName,
}: {
  judge: JudgeProfile;
  stages: JudgeStageSummary[];
  eventName: string | null;
}) {
  usePoll(10000, { only: ['stages'] });
  const firstName = judge.fullName.split(' ')[0];

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4 py-12">
      <div className="text-center">
        <Heading first="Hola," second={firstName} />
        {eventName && (
          <p className="mt-4 text-primary/70 text-sm">Jurado en {eventName}</p>
        )}
      </div>

      {stages.length === 0 ? (
        <div className="border-2 border-primary/40 p-6 text-center text-primary/70 text-sm">
          Todavía no tienes stages asignados. Avísale al equipo de Platanus Hack.
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-primary/70 text-sm">
            Elige el stage que vas a evaluar:
          </p>
          {stages.map((stage) => (
            <Link
              key={stage.id}
              href={`/judges/stages/${stage.slug}`}
              className="group flex items-center justify-between gap-4 border-2 border-primary p-5 transition-colors hover:bg-primary/10"
            >
              <div className="flex min-w-0 flex-col gap-2">
                <h2 className="font-bold text-primary text-xl">{stage.name}</h2>
                <p className="text-primary/70 text-xs">
                  {stage.passedCount}/{stage.projectCount} presentados ·{' '}
                  {stage.gradedCount}/{stage.projectCount} evaluados por ti
                </p>
                {stage.completedAt && (
                  <span className="flex w-fit items-center gap-1 border border-primary px-2 py-1 font-bold text-primary text-xs">
                    <CheckCircle2 className="h-3 w-3" />
                    Evaluación cerrada
                  </span>
                )}
                {!stage.completedAt && stage.currentProjectName && (
                  <span className="flex w-fit items-center gap-1 bg-primary px-2 py-1 font-bold text-background text-xs">
                    <Mic className="h-3 w-3" />
                    En escenario: {stage.currentProjectName}
                  </span>
                )}
              </div>
              <ChevronRight className="h-6 w-6 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
